/**
 * ADMIN MODULE — LinkedIn Flywheel
 * 2x2 panel grid (stacks on mobile): Post Queue, Warm Leads, Comment Queue, CTA
 * Status. Reads/writes go through flywheelService. Realtime subscriptions on
 * linkedin_posts + comment_queue keep the UI live as the Python scripts write.
 *
 * Table → panel map:
 *   linkedin_posts      → Panel 1 (Post Queue)
 *   linkedin_engagement → Panel 2 (Warm Leads) + Panel 4 conversion metric
 *   comment_queue       → Panel 3 (Comment Queue)
 *   conversion_config   → Panel 4 (CTA Status)
 */

import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../../shared/utils/supabaseClient.js";
import { findAgentByName, createIssue } from "../utils/paperclipClient.js";
import AdminLayout from "../components/AdminLayout.jsx";

const WRITER_AGENT_NAME = "LinkedIn Writer";

// "Generate now" — creates a Paperclip task assigned to the writer agent, which
// wakes it to draft posts on the Claude subscription. New drafts arrive live via
// the realtime subscription on linkedin_posts. No Python, no leaving the board.
function GenerateButton() {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  async function generate() {
    setBusy(true); setMsg(null);
    const { data: agent, error: aErr } = await findAgentByName(WRITER_AGENT_NAME);
    if (aErr || !agent) {
      setBusy(false);
      setMsg(aErr || "Writer agent not found. Is Paperclip running?");
      return;
    }
    const { error } = await createIssue(
      "Generate this week's LinkedIn posts",
      "Run your weekly routine per HEARTBEAT.md: read persona, lessons, and topics; "
        + "write up to 4 posts (one per type) to post.txt as UTF-8 and insert each with "
        + "agent_tools.py draft. Report the draft ids.",
      agent.id,
    );
    setBusy(false);
    setMsg(error ? `Could not start generation: ${error}` : "Generation started — new drafts will appear here shortly.");
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <button className="btn" onClick={generate} disabled={busy}>{busy ? "Starting…" : "✨ Generate now"}</button>
      {msg && <span className="admin-panel-count" style={{ color: "var(--muted)" }}>{msg}</span>}
    </div>
  );
}
import {
  listPosts, setPostStatus, updatePostContent, recordWritingLesson, POST_STATUSES, POST_TYPES_ALL,
  setPostUrl, markPublished,
  listWarmLeads, listEngagementForProfile,
  listPendingComments, approveComment, skipComment,
  getActiveCta, countConverted, updateCta,
  getSettings, updateSettings, slugifyTypeKey,
} from "../utils/flywheelService.js";

// Capture a post's live LinkedIn URL + mark it published. The URL is what the
// engagement scraper watches. Shown on non-draft posts (approved/queued/published).
function PublishControls({ post, reload }) {
  const [url, setUrl] = useState(post.linkedin_post_url || "");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const published = post.status === "published";

  async function run(fn) {
    setBusy(true); setMsg(null);
    try { await fn(); await reload(); }
    catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  }
  const safe = /^https?:\/\//i.test(url) ? url : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }} onClick={(e) => e.stopPropagation()}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Live LinkedIn post URL"
          style={{ flex: "1 1 240px", minWidth: 0 }}
        />
        <button className="secondary" disabled={busy} onClick={() => run(() => setPostUrl(post.id, url))}>Save URL</button>
        {!published && (
          <button className="btn" disabled={busy} onClick={() => run(() => markPublished(post.id, url))}>Mark published</button>
        )}
        {published && safe && <a href={safe} target="_blank" rel="noreferrer" className="admin-row-meta">Open ↗</a>}
      </div>
      {msg && <span className="admin-row-meta" style={{ color: "var(--warn)" }}>{msg}</span>}
    </div>
  );
}

// Only ever open validated http(s) profile links.
function safeUrl(u) {
  if (typeof u !== "string") return null;
  return /^https?:\/\//i.test(u) ? u : null;
}
function fmtDate(t) {
  if (!t) return null;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}
const STAGE_LABEL = { pre_launch: "Pre-launch", live: "Product live", new_module: "New module" };

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const TYPE_LABEL = { pain_story: "Pain story", education: "Education", social_proof: "Social proof", hot_take: "Hot take" };

// Image-style presets the Image Designer renders well. Picking one fills the type's image-guidance
// box (still editable). Keeps the founder from memorizing the options.
const IMAGE_STYLE_PRESETS = [
  { label: "Candid documentary photo", brief: "Candid documentary photograph of a real, slightly messy real-world moment. Shallow depth of field, natural window light, warm muted tones, honest photojournalism feel — not staged or perfect." },
  { label: "Cinematic / aspirational photo", brief: "Cinematic, aspirational photograph — a bright, polished real-world scene that signals momentum and success. Warm optimistic golden-hour light, shallow depth of field, premium editorial feel." },
  { label: "Bold flat vector illustration", brief: "Bold flat vector illustration turning the idea into a simple visual metaphor. Limited 2–3 colour palette, strong geometric shapes, lots of negative space, modern startup-blog look. Conceptual, not literal." },
  { label: "Isometric 3D render", brief: "Isometric 3D render — clean clay/plastic materials, soft studio shadows, one tidy scene viewed at a 30° angle." },
  { label: "Conceptual still-life (props)", brief: "Conceptual still-life — real physical objects arranged as a visual metaphor on a seamless studio backdrop, crisp product lighting." },
  { label: "Hand-drawn ink sketch", brief: "Hand-drawn ink illustration — loose expressive linework with a single selective spot colour, sketchbook paper texture, warm and human." },
  { label: "Surreal / conceptual", brief: "Bold surreal/conceptual image with one striking metaphor or unexpected twist (unusual scale, an inverted scene), dramatic high-contrast minimalism — built to stop the scroll." },
  { label: "Minimalist macro", brief: "Minimalist macro — one hero object filling the frame against lots of empty space, dramatic single-source light, rich surface texture." },
  { label: "Editorial paper-collage", brief: "Editorial paper-collage — layered cut-paper with torn edges and subtle textures, a tactile magazine-spread look." },
];

// Editable flywheel controls: posts/week, type mix, posting cadence (days/times),
// and the auto-generate toggle. The writer agent reads posts_per_week + post_types;
// the schedule drives when approved posts publish.
function FlywheelSettings() {
  const [s, setS] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    let alive = true;
    getSettings().then((d) => { if (alive) setS(d); }).catch((e) => setMsg(e.message));
    return () => { alive = false; };
  }, []);

  if (!s) return null;

  const set = (patch) => setS({ ...s, ...patch });
  const sched = s.schedule || [];
  const setSlot = (i, patch) => set({ schedule: sched.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const addSlot = () => set({ schedule: [...sched, { day: 1, time: "09:00", post_type: null }] });
  const removeSlot = (i) => set({ schedule: sched.filter((_, j) => j !== i) });
  const defs = s.type_defs || [];
  const setDef = (i, patch) => set({ type_defs: defs.map((d, j) => (j === i ? { ...d, ...patch } : d)) });
  const addDef = () => set({ type_defs: [...defs, { key: "", label: "New Type", text_brief: "", image_brief: "", enabled: true }] });
  const removeDef = (i) => set({ type_defs: defs.filter((_, j) => j !== i) });
  const enabledDefs = defs.filter((d) => d.enabled !== false);

  async function save() {
    setBusy(true); setMsg(null);
    try {
      await updateSettings(s.id, {
        posts_per_week: s.posts_per_week,
        type_defs: s.type_defs || [],
        schedule: s.schedule, auto_generate: s.auto_generate,
      });
      setMsg("Saved.");
    } catch (e) { setMsg(e.message); }
    finally { setBusy(false); }
  }

  return (
    <section className="admin-card" style={{ marginBottom: 18 }}>
      <div className="admin-panel-head" style={{ cursor: "pointer", margin: 0 }} onClick={() => setOpen((o) => !o)}>
        <h2>⚙ Flywheel Settings</h2>
        <span className="admin-panel-count">
          {s.posts_per_week}/week · {sched.length} slots · auto-gen {s.auto_generate ? "on" : "off"} {open ? "▲" : "▼"}
        </span>
      </div>

      {open && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 14 }}>
          <label className="admin-field" style={{ maxWidth: 220 }}>
            <span>Posts per week</span>
            <input type="number" min={1} max={21} value={s.posts_per_week}
              onChange={(e) => set({ posts_per_week: Number(e.target.value) })} />
          </label>

          <div>
            <div className="admin-row-meta" style={{ marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Post types — enable, rename, and steer the text &amp; image for each. Add your own to enter the rotation.</span>
              <button type="button" className="secondary" onClick={addDef} style={{ fontSize: 12, padding: "4px 10px" }}>+ Add post type</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {defs.length === 0 && <div className="admin-empty">No post types yet — add one.</div>}
              {defs.map((d, i) => (
                <div key={i} className="admin-card" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, whiteSpace: "nowrap" }}>
                      <input type="checkbox" checked={d.enabled !== false}
                        onChange={(e) => setDef(i, { enabled: e.target.checked })} style={{ width: "auto" }} />
                      In rotation
                    </label>
                    <input type="text" value={d.label || ""} placeholder="Type name — e.g. Pain Story"
                      onChange={(e) => setDef(i, { label: e.target.value })} style={{ flex: 1, minWidth: 160 }} />
                    <button type="button" className="danger" onClick={() => removeDef(i)} style={{ fontSize: 12, padding: "4px 10px" }}>Remove</button>
                  </div>
                  <label className="admin-field" style={{ marginBottom: 0 }}>
                    <span>Writing guidance (steers the writer)</span>
                    <textarea value={d.text_brief || ""} onChange={(e) => setDef(i, { text_brief: e.target.value })}
                      placeholder="Angles to hit, domains to stay inside, tone, things to avoid…" style={{ minHeight: 56 }} />
                  </label>
                  <label className="admin-field" style={{ marginBottom: 0 }}>
                    <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span>Image guidance (steers the Image Designer for this type)</span>
                      <select value="" title="Fill with a style preset (you can still edit it)"
                        onChange={(e) => { const p = IMAGE_STYLE_PRESETS.find((x) => x.label === e.target.value); if (p) setDef(i, { image_brief: p.brief }); }}
                        style={{ maxWidth: 210, fontSize: 12 }}>
                        <option value="">＋ Style preset…</option>
                        {IMAGE_STYLE_PRESETS.map((p) => <option key={p.label} value={p.label}>{p.label}</option>)}
                      </select>
                    </span>
                    <textarea value={d.image_brief || ""} onChange={(e) => setDef(i, { image_brief: e.target.value })}
                      placeholder="Pick a Style preset above, or describe a medium — e.g. candid documentary photo · bold flat illustration · isometric 3D…" style={{ minHeight: 56 }} />
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="admin-row-meta" style={{ marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Posting cadence — which days &amp; times</span>
              <button type="button" className="secondary" onClick={addSlot} style={{ fontSize: 12, padding: "4px 10px" }}>+ Add slot</button>
            </div>
            <div className="admin-list">
              {sched.length === 0 && <div className="admin-empty">No slots yet — add one.</div>}
              {sched.map((slot, i) => (
                <div key={i} className="admin-row" style={{ flexDirection: "row", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <select value={slot.day} onChange={(e) => setSlot(i, { day: Number(e.target.value) })} style={{ maxWidth: 110 }}>
                    {DAY_NAMES.map((d, di) => <option key={di} value={di}>{d}</option>)}
                  </select>
                  <input type="time" value={slot.time} onChange={(e) => setSlot(i, { time: e.target.value })} style={{ maxWidth: 130 }} />
                  <select value={slot.post_type || ""} onChange={(e) => setSlot(i, { post_type: e.target.value || null })} style={{ maxWidth: 150 }}>
                    <option value="">Any type</option>
                    {enabledDefs.map((d) => { const k = slugifyTypeKey(d.label); return <option key={k} value={k}>{d.label}</option>; })}
                  </select>
                  <button type="button" className="danger" onClick={() => removeSlot(i)} style={{ fontSize: 12, padding: "4px 10px" }}>Remove</button>
                </div>
              ))}
            </div>
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14 }}>
            <input type="checkbox" checked={s.auto_generate} onChange={(e) => set({ auto_generate: e.target.checked })}
              style={{ width: "auto" }} />
            Auto-generate drafts on this cadence
          </label>
          <div className="admin-row-meta" style={{ marginTop: -8 }}>
            When on, the scheduler creates a generate-task on your cadence (the scheduler is a separate
            background piece — until it's wired, use “✨ Generate now” below).
          </div>

          {sched.length !== s.posts_per_week && (
            <div className="admin-row-meta" style={{ color: "var(--warn)" }}>
              Heads up: {sched.length} cadence slot(s) but {s.posts_per_week} posts/week — consider matching them.
            </div>
          )}

          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="btn" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save settings"}</button>
            {msg && <span className="admin-panel-count">{msg}</span>}
          </div>
        </div>
      )}
    </section>
  );
}

const IMAGE_AGENT_NAME = "Image Designer";

// Per-post image: shows the generated image + a button that asks the Image Designer
// agent (via Paperclip) to read the post, write a prompt, and render it with Gemini.
function PostImage({ post }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const has = !!post.image_url;

  async function generate(e) {
    e.stopPropagation();
    setBusy(true); setMsg(null);
    const { data: agent, error: aErr } = await findAgentByName(IMAGE_AGENT_NAME);
    if (aErr || !agent) { setBusy(false); setMsg(aErr || "Image Designer agent not found in Paperclip."); return; }
    const { error } = await createIssue(
      `Generate image for post ${post.id}`,
      `Per your HEARTBEAT: read post ${post.id} (image_tools.py context ${post.id}), write an on-brand image prompt to prompt.txt, then image_tools.py generate ${post.id} prompt.txt.`,
      agent.id,
    );
    setBusy(false);
    setMsg(error ? `Could not start: ${error}` : "Image requested — it'll appear here shortly.");
  }

  return (
    <div style={{ marginTop: 6 }} onClick={(e) => e.stopPropagation()}>
      {has && (
        <img src={post.image_url} alt={post.image_prompt || "post image"} title={post.image_prompt || ""}
          style={{ width: "100%", maxHeight: 220, objectFit: "cover", borderRadius: "var(--r-control)", border: "1px solid var(--border)" }} />
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
        <button className="secondary" disabled={busy} onClick={generate} style={{ fontSize: 12, padding: "5px 11px" }}>
          {busy ? "Requesting…" : has ? "🖼 Regenerate image" : "🖼 Generate image"}
        </button>
        {post.image_status === "pending" && <span className="admin-row-meta">generating…</span>}
        {post.image_status === "failed" && <span className="admin-row-meta" style={{ color: "var(--warn)" }}>last attempt failed</span>}
        {msg && <span className="admin-row-meta">{msg}</span>}
      </div>
    </div>
  );
}

/* ─────────────────────────── Panel 1: Post Queue ─────────────────────────── */
function PostQueue({ posts, reload }) {
  const [tab, setTab] = useState("draft");
  const [open, setOpen] = useState(null);   // expanded post id
  const [editing, setEditing] = useState(null); // { id, content }
  const [busy, setBusy] = useState(false);

  const filtered = posts.filter((p) => p.status === tab);

  async function act(id, status) {
    setBusy(true);
    try { await setPostStatus(id, status); await reload(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }
  async function saveEdit() {
    setBusy(true);
    try {
      // Capture the edit as a lesson for the writer agent (only if it changed).
      if (editing.content !== editing.original) {
        await recordWritingLesson({
          post_id: editing.id,
          post_type: editing.postType,
          original_text: editing.original,
          edited_text: editing.content,
        });
      }
      await updatePostContent(editing.id, editing.content);
      await setPostStatus(editing.id, "approved");
      setEditing(null); await reload();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }

  return (
    <section className="admin-card">
      <div className="admin-panel-head"><h2>Post Queue</h2><span className="admin-panel-count">{posts.length} total</span></div>
      <div style={{ marginBottom: 12 }}><GenerateButton /></div>
      <div className="admin-tabs">
        {POST_STATUSES.map((s) => (
          <button key={s} className={`admin-tab${tab === s ? " active" : ""}`} onClick={() => setTab(s)}>
            {s[0].toUpperCase() + s.slice(1)} ({posts.filter((p) => p.status === s).length})
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="admin-empty">No {tab} posts.</div>
      ) : (
        <div className="admin-list">
          {filtered.map((p) => {
            const expanded = open === p.id;
            const content = p.draft_content || "(empty)";
            return (
              <div className="admin-row clickable" key={p.id} onClick={() => setOpen(expanded ? null : p.id)}>
                <div className="admin-row-top">
                  {p.post_type && <span className={`admin-badge ${p.post_type}`}>{p.post_type.replace("_", " ")}</span>}
                  <span className="admin-row-meta">{fmtDate(p.scheduled_for) ? `Scheduled ${fmtDate(p.scheduled_for)}` : "Not scheduled"}</span>
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                  {expanded ? content : content.slice(0, 120) + (content.length > 120 ? "…" : "")}
                </div>
                <PostImage post={p} />
                {p.status === "draft" && (
                  <div className="admin-row-actions" onClick={(e) => e.stopPropagation()}>
                    <button className="btn" disabled={busy} onClick={() => act(p.id, "approved")}>Approve</button>
                    <button className="secondary" disabled={busy} onClick={() => setEditing({ id: p.id, content, original: content, postType: p.post_type })}>Edit</button>
                    <button className="danger" disabled={busy} onClick={() => act(p.id, "rejected")}>Reject</button>
                  </div>
                )}
                {(p.status === "approved" || p.status === "queued" || p.status === "published") && (
                  <PublishControls post={p} reload={reload} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Edit post</h3>
            <label className="admin-field"><span>Content</span>
              <textarea value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} style={{ minHeight: 200 }} autoFocus />
            </label>
            <div className="modal-actions">
              <button className="secondary" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn" disabled={busy} onClick={saveEdit}>Save &amp; approve</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ─────────────────────────── Panel 2: Warm Leads ─────────────────────────── */
function LeadHistory({ profileUrl }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    let alive = true;
    listEngagementForProfile(profileUrl).then((r) => { if (alive) setRows(r); }).catch(() => setRows([]));
    return () => { alive = false; };
  }, [profileUrl]);
  if (rows == null) return <div className="admin-row-meta">Loading history…</div>;
  if (rows.length === 0) return <div className="admin-row-meta">No history.</div>;
  return (
    <div className="admin-runs">
      {rows.map((r) => (
        <div className="admin-run" key={r.id}>
          <span>{r.engagement_type || "engagement"}{r.comment_text ? `: “${r.comment_text.slice(0, 60)}”` : ""}</span>
          <span>{fmtDate(r.scraped_at)}</span>
        </div>
      ))}
    </div>
  );
}

function WarmLeads({ leads }) {
  const [open, setOpen] = useState(null);
  return (
    <section className="admin-card">
      <div className="admin-panel-head"><h2>Warm Leads</h2><span className="admin-panel-count">{leads.length} leads</span></div>
      {leads.length === 0 ? (
        <div className="admin-empty">No warm leads yet.</div>
      ) : (
        <div className="admin-list">
          {leads.map((l) => {
            const url = safeUrl(l.linkedin_profile_url);
            const hot = l.lead_status === "hot";
            const expanded = open === l.id;
            return (
              <div className={`admin-row clickable${hot ? " hot" : ""}`} key={l.id} onClick={() => setOpen(expanded ? null : l.id)}>
                <div className="admin-row-top">
                  <span className="admin-row-title">{l.profile_name || "Unknown"}</span>
                  <span className="admin-row-meta">
                    <span className="admin-badge">{l.engagement_count}×</span>
                    {l.lead_status && <span className={`admin-badge ${l.lead_status}`}>{l.lead_status}</span>}
                  </span>
                </div>
                <div className="admin-row-meta">
                  {url ? <a href={url} target="_blank" rel="noreferrer">View profile ↗</a> : <span>No profile URL</span>}
                  <span>Last seen {fmtDate(l.scraped_at)}</span>
                </div>
                {expanded && <LeadHistory profileUrl={l.linkedin_profile_url} />}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ────────────────────────── Panel 3: Comment Queue ───────────────────────── */
function CommentQueue({ comments, reload, optimisticRemove }) {
  const [editing, setEditing] = useState({});  // id -> draft text being edited
  const [busy, setBusy] = useState(null);

  async function approve(c) {
    setBusy(c.id);
    const reply = editing[c.id] != null ? editing[c.id] : c.draft_reply;
    optimisticRemove(c.id);                    // move out of queue immediately
    try { await approveComment(c.id, reply); }
    catch (e) { alert(e.message); await reload(); }
    finally { setBusy(null); }
  }
  async function skip(c) {
    setBusy(c.id);
    optimisticRemove(c.id);
    try { await skipComment(c.id); }
    catch (e) { alert(e.message); await reload(); }
    finally { setBusy(null); }
  }

  return (
    <section className="admin-card">
      <div className="admin-panel-head"><h2>Comment Queue</h2><span className="admin-panel-count">{comments.length} pending</span></div>
      {comments.length === 0 ? (
        <div className="admin-empty">Nothing waiting for approval. 🎉</div>
      ) : (
        <div className="admin-list">
          {comments.map((c) => {
            const isEditing = editing[c.id] != null;
            return (
              <div className="admin-row" key={c.id}>
                <div className="admin-quote">{c.their_comment || "(no comment text)"}</div>
                {isEditing ? (
                  <textarea
                    className="admin-draft" value={editing[c.id]} autoFocus
                    onChange={(e) => setEditing({ ...editing, [c.id]: e.target.value })}
                    style={{ width: "100%", minHeight: 80 }}
                  />
                ) : (
                  <div className="admin-draft">{c.draft_reply || "(no draft)"}</div>
                )}
                <div className="admin-row-actions">
                  <button className="btn" disabled={busy === c.id} onClick={() => approve(c)}>✅ Approve</button>
                  {isEditing ? (
                    <button className="secondary" onClick={() => { const n = { ...editing }; delete n[c.id]; setEditing(n); }}>Cancel edit</button>
                  ) : (
                    <button className="secondary" onClick={() => setEditing({ ...editing, [c.id]: c.draft_reply || "" })}>✏️ Edit</button>
                  )}
                  <button className="danger" disabled={busy === c.id} onClick={() => skip(c)}>✗ Skip</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* ────────────────────────── Panel 4: CTA Status ──────────────────────────── */
function CtaStatus({ cta, converted, reload }) {
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ launch_stage: "pre_launch", cta_text: "", cta_url: "" });
  const [busy, setBusy] = useState(false);

  function openModal() {
    setForm({
      launch_stage: cta?.launch_stage || "pre_launch",
      cta_text: cta?.cta_text || "",
      cta_url: cta?.cta_url || "",
    });
    setModal(true);
  }
  async function save() {
    setBusy(true);
    try { await updateCta(form); setModal(false); await reload(); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }
  const url = safeUrl(cta?.cta_url);

  return (
    <section className="admin-card">
      <div className="admin-panel-head"><h2>CTA Status</h2>
        {cta && <span className={`admin-badge ${cta.launch_stage === "pre_launch" ? "warm" : "social_proof"}`}>{STAGE_LABEL[cta.launch_stage] || cta.launch_stage}</span>}
      </div>
      {cta ? (
        <>
          <div className="admin-kv">
            <div className="row"><span className="k">CTA text</span><span className="v">{cta.cta_text || "—"}</span></div>
            <div className="row"><span className="k">Destination</span><span className="v">{url ? <a href={url} target="_blank" rel="noreferrer">{cta.cta_url}</a> : (cta.cta_url || "—")}</span></div>
          </div>
          <div className="admin-metric">
            <span className="num">{converted == null ? "—" : converted}</span>
            <span className="lbl">conversions tracked</span>
          </div>
          <div className="admin-row-actions" style={{ marginTop: 14 }}>
            <button className="btn" onClick={openModal}>Update CTA</button>
          </div>
        </>
      ) : (
        <div className="admin-empty">No active CTA configured.</div>
      )}

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Update CTA</h3>
            <p className="modal-message">Sets the current active CTA. The previous one is retired.</p>
            <div style={{ marginTop: 16 }}>
              <label className="admin-field"><span>Launch stage</span>
                <select value={form.launch_stage} onChange={(e) => setForm({ ...form, launch_stage: e.target.value })}>
                  <option value="pre_launch">Pre-launch</option>
                  <option value="live">Product live</option>
                  <option value="new_module">New module</option>
                </select>
              </label>
              <label className="admin-field"><span>CTA text</span>
                <input value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} maxLength={500} />
              </label>
              <label className="admin-field"><span>CTA URL</span>
                <input value={form.cta_url} onChange={(e) => setForm({ ...form, cta_url: e.target.value })} maxLength={1000} placeholder="https://…" />
              </label>
            </div>
            <div className="modal-actions">
              <button className="secondary" onClick={() => setModal(false)}>Cancel</button>
              <button className="btn" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save CTA"}</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/* ──────────────────────────────── View ───────────────────────────────────── */
export default function LinkedInFlywheelView() {
  const [posts, setPosts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [comments, setComments] = useState([]);
  const [cta, setCta] = useState(null);
  const [converted, setConverted] = useState(null);
  const [err, setErr] = useState(null);

  const reloadPosts = useCallback(async () => { try { setPosts(await listPosts()); } catch (e) { setErr(e.message); } }, []);
  const reloadComments = useCallback(async () => { try { setComments(await listPendingComments()); } catch (e) { setErr(e.message); } }, []);
  const reloadCta = useCallback(async () => {
    try { const [c, n] = await Promise.all([getActiveCta(), countConverted()]); setCta(c); setConverted(n); }
    catch (e) { setErr(e.message); }
  }, []);
  const reloadLeads = useCallback(async () => { try { setLeads(await listWarmLeads()); } catch (e) { setErr(e.message); } }, []);

  useEffect(() => { reloadPosts(); reloadLeads(); reloadComments(); reloadCta(); }, [reloadPosts, reloadLeads, reloadComments, reloadCta]);

  // Realtime — refresh the affected panel when the Python scripts write.
  useEffect(() => {
    const ch = supabase
      .channel("admin-flywheel")
      .on("postgres_changes", { event: "*", schema: "public", table: "linkedin_posts" }, reloadPosts)
      .on("postgres_changes", { event: "*", schema: "public", table: "comment_queue" }, reloadComments)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [reloadPosts, reloadComments]);

  const optimisticRemove = useCallback((id) => setComments((cs) => cs.filter((c) => c.id !== id)), []);

  return (
    <AdminLayout title="LinkedIn Flywheel" subtitle="Content, leads, and the conversion loop.">
      {err && <div className="admin-banner" role="alert"><span>{err}</span></div>}
      <FlywheelSettings />
      <div className="admin-panel-grid">
        <PostQueue posts={posts} reload={reloadPosts} />
        <WarmLeads leads={leads} />
        <CommentQueue comments={comments} reload={reloadComments} optimisticRemove={optimisticRemove} />
        <CtaStatus cta={cta} converted={converted} reload={reloadCta} />
      </div>
    </AdminLayout>
  );
}
